<template>
    <Toast />

    <div class="main-container px-2 md:px-16">
        <div class="grid grid-cols-12 gap-8">
            <!-- First row -->
            <div class="col-span-12">
                <div class="flex sm:flex-row items-center justify-between  mt-5">
                    <div class="flex flex-col items-center sm:items-start" v-if="user">
                        <span class="text-900 font-bold text-4xl">{{ user.name }}</span>
                        <p class="text-600 m-0"> Last logged in on {{ getFormattedLoggedInDate(user.lastLogin) }}</p>
                    </div>

                </div>
            </div>

            <!-- Second row -->
            <div class="col-span-12 md:col-span-6 xl:col-span-4 ">
                <Card class="h-full card-bg">
                    <template #title><span class="font-semibold text-lg">Default System Commission</span> </template>
                    <template #content>
                        <div v-if="commission !== null && !commissionEdit">
                            <p v-tooltip.bottom="'Click to change'" @click="showEditCommission"
                                class="text-lg text-center  cursor-pointer">{{ commission }}%</p>
                        </div>
                        <div v-if="commissionEdit" class="flex items-center space-x-4">
                            <InputNumber v-model="commission" inputId="new-commission" suffix="%" :minFractionDigits="2"
                                :step="0.25" class="w-full" />
                            <Button :disabled="!hasCommissionChanged" label="Save" icon="pi pi-check" iconPos="right" @click="saveCommission"/>
                        </div>
                        <div v-if="commission === null">
                            <p>Loading commission...</p>
                        </div>
                    </template>
                </Card>
            </div>
            <!-- Documentation tiles — one per doc so admins can jump straight to
                 the topic they need instead of hunting through /help. -->
            <div class="col-span-12 mt-2">
                <h2 class="text-xl font-semibold mb-3">Documentation</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <router-link
                        :to="{ name: 'PostbackSetupGuide' }"
                        class="doc-tile card-bg"
                    >
                        <div class="doc-tile-icon"><i class="pi pi-send"></i></div>
                        <div>
                            <p class="doc-tile-title">Setting up Postbacks</p>
                            <p class="doc-tile-desc">Global &amp; per site-link postback URLs, and the {clickid} placeholder.</p>
                        </div>
                    </router-link>

                    <router-link
                        to="/help"
                        class="doc-tile card-bg"
                    >
                        <div class="doc-tile-icon"><i class="pi pi-link"></i></div>
                        <div>
                            <p class="doc-tile-title">Setting up Tracking Links</p>
                            <p class="doc-tile-desc">Per-affiliate-system tracking link setup and internal-note conventions.</p>
                        </div>
                    </router-link>

                    <router-link
                        :to="{ name: 'AdjustmentGuide' }"
                        class="doc-tile card-bg"
                    >
                        <div class="doc-tile-icon"><i class="pi pi-sliders-h"></i></div>
                        <div>
                            <p class="doc-tile-title">Making Adjustments</p>
                            <p class="doc-tile-desc">Right-click a campaign row on Site Earnings to add or subtract stats.</p>
                        </div>
                    </router-link>
                </div>
            </div>

            <!-- Quick how-to: sending a test reg postback (admins) -->
            <div class="col-span-12">
                <div class="doc-howto card-bg">
                    <div class="flex items-center gap-2 mb-1">
                        <i class="pi pi-bolt doc-howto-icon"></i>
                        <h2 class="text-xl font-semibold m-0">Sending a test reg postback</h2>
                    </div>
                    <p class="text-600 text-sm mb-3">Replay a real registration on a publisher's link to confirm it routes to us and forwards to the advertiser. Admins only — the send forwards a <strong>real</strong> conversion.</p>
                    <ol class="doc-steps">
                        <li>Open <router-link to="/postbacks?view=testlink" class="doc-link">Postbacks &rarr; Test link</router-link>.</li>
                        <li>Paste the <strong>publisher's own tracking link</strong> for the brand and click <strong>Run</strong>. It follows the full redirect chain (incl. meta-refresh / JS hops), detects the tracker, click&nbsp;id and campaign key, matches our site link, and shows the exact inbound postback we expect.</li>
                        <li>Check the <strong>Matched link</strong> (advertiser, publisher, salid) and the resolved <strong>click id</strong>. Always use the publisher's real link so the click id is real &mdash; a made-up id is accepted by us but the advertiser answers <strong>400</strong>.</li>
                        <li>To fire it for real: turn <strong>off &ldquo;Preview only&rdquo;</strong>, click <strong>Send test reg</strong>, and confirm. It sends the registration through our postback service and reads back the advertiser's response.</li>
                        <li><strong>pb 200</strong> = we accepted it; <strong>advertiser 200</strong> = accepted; <strong>400</strong> = usually a stale/fake click id &mdash; resolve a fresh link and retry.</li>
                    </ol>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, reactive } from 'vue';
import { useStore } from 'vuex';
import { getFormattedLoggedInDate } from '@/utils/dateHelper';
import { useToast } from "primevue/usetoast";
import systemDefaultsService from '@/services/SystemDefaultsService';
const toast = useToast();

const store = useStore();
const user = computed(() => store.state.user);
const commission = ref(null);
const commissionEdit = ref(false);
const originalCommission = ref(0);

const hasCommissionChanged = computed(() => {
  return commission.value !== originalCommission.value;
});

const showEditCommission = () => commissionEdit.value = !commissionEdit.value;

const saveCommission = async () => {
  try {
    const response = await systemDefaultsService.updateCommission(commission.value);
    originalCommission.value = response.data.defaultCommission;
    commissionEdit.value = false;
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Default commission updated successfully!',
      life: 3000
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update commission.',
      life: 3000
    });
    console.error('Error updating commission:', error);
  }
};

onMounted(async () => {
    try {
        const response = await systemDefaultsService.getDefaults();
        console.log(response);
        commission.value = response.data.defaultCommission;
        originalCommission.value = response.data.defaultCommission;
    } catch (error) {
        console.error('Error fetching system defaults:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load system defaults.',
            life: 3000
        });
    }
});

</script>

<style scoped>
.doc-tile {
    display: flex;
    align-items: flex-start;
    gap: 0.9rem;
    padding: 1rem 1.1rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: inherit;
    text-decoration: none;
    transition: transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
    cursor: pointer;
}
.doc-tile:hover {
    transform: translateY(-1px);
    border-color: var(--primary-color, #3b82f6);
    background-color: rgba(59, 130, 246, 0.06);
}
.doc-tile-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(59, 130, 246, 0.15);
    color: var(--primary-color, #3b82f6);
    font-size: 1.15rem;
    flex-shrink: 0;
}
.doc-tile-title {
    font-weight: 600;
    margin: 0 0 0.2rem 0;
}
.doc-tile-desc {
    font-size: 0.8rem;
    color: #9ca3af;
    line-height: 1.35;
    margin: 0;
}
.doc-howto {
    padding: 1.1rem 1.25rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
}
.doc-howto-icon { color: var(--primary-color, #3b82f6); font-size: 1.1rem; }
.doc-steps {
    margin: 0;
    padding-left: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-size: 0.9rem;
    line-height: 1.45;
}
.doc-steps li { padding-left: 0.25rem; }
.doc-link { color: var(--primary-color, #3b82f6); text-decoration: none; font-weight: 600; }
.doc-link:hover { text-decoration: underline; }
</style>